import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Spinner de carga reutilizable con 3 variantes visuales.
 *
 * - `inline`  : Spinner pequeno (18px) para usar dentro de botones o junto a texto.
 * - `zone`    : Spinner mediano (28px) con texto opcional, para drop-zones o secciones.
 * - `overlay` : Modal a pantalla completa con fondo oscuro semitransparente y card centrada.
 *
 * Uso basico:
 *   <app-loading-spinner />                              <!-- inline por defecto -->
 *   <app-loading-spinner variant="zone" label="Subiendo archivo..." />
 *   <app-loading-spinner variant="overlay" label="Cargando..." />
 */
@Component({
  selector: 'app-loading-spinner',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (variant) {
      @case ('overlay') {
        <div class="spinner-overlay">
          <div class="spinner-card">
            <div class="spinner spinner--lg"></div>
            @if (label) {
              <p class="spinner-label">{{ label }}</p>
            }
          </div>
        </div>
      }
      @case ('zone') {
        <div class="spinner-zone">
          <div class="spinner spinner--md"></div>
          @if (label) {
            <span class="spinner-label spinner-label--sm">{{ label }}</span>
          }
        </div>
      }
      @default {
        <div class="spinner spinner--sm"></div>
      }
    }
  `,
  styles: [`
    :host { display: contents; }

    .spinner {
      border-radius: 50%;
      border-style: solid;
      border-color: var(--color-inactive-tint, #E4E6E7);
      border-top-color: var(--color-secondary, #157BF8);
      animation: spin 0.75s linear infinite;
      flex-shrink: 0;
    }

    .spinner--sm {
      width: 18px;
      height: 18px;
      border-width: 2.5px;
    }

    .spinner--md {
      width: 28px;
      height: 28px;
      border-width: 3px;
    }

    .spinner--lg {
      width: 48px;
      height: 48px;
      border-width: 4px;
    }

    /* ─── Variante: overlay — modal a pantalla completa ─── */
    .spinner-overlay {
      position: fixed;
      inset: 0;
      background: var(--color-overlay-40, rgba(0, 0, 0, 0.40));
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .spinner-card {
      background: var(--color-background-card, #fff);
      border-radius: var(--border-radius-lg, 16px);
      padding: var(--spacing-2xl, 32px) var(--spacing-3xl, 40px);
      box-shadow: var(--shadow-card);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-md, 16px);
    }

    /* ─── Variante: zone ─── */
    .spinner-zone {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs, 8px);
      padding: var(--spacing-xs, 8px) 0;
    }

    .spinner-label {
      font-family: var(--font-family-primary, 'Montserrat', sans-serif);
      font-size: var(--font-size-base, 16px);
      font-weight: var(--font-weight-medium, 500);
      color: var(--color-text-primary);
      margin: 0;
    }

    .spinner-label--sm {
      font-size: var(--font-size-sm, 12px);
      font-weight: var(--font-weight-regular, 400);
      color: var(--color-text-secondary);
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `]
})
export class LoadingSpinnerComponent {
  @Input() variant: 'inline' | 'zone' | 'overlay' = 'inline';
  @Input() label = '';
}
